// ── B2BClients.jsx ────────────────────────────────────────────────────────────
import { useEffect, useState, useCallback } from "react";
import { b2bAPI } from "../services/api";
import { RefreshCw, CheckCircle, XCircle, Key } from "lucide-react";
import toast from "react-hot-toast";
import { format } from "date-fns";

export default function B2BClients() {
  const [clients, setClients] = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await b2bAPI.clients({ page, limit: 15, status: statusFilter });
      setClients(r.data.data); setTotal(r.data.pagination?.total||0);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(()=>{ load(); },[load]);

  const approve = async (id) => {
    try { await b2bAPI.approve(id); toast.success("Client approved ✅"); load(); }
    catch { toast.error("Failed"); }
  };

  const suspend = async (id) => {
    try { await b2bAPI.suspend(id); toast.success("Client suspended"); load(); }
    catch { toast.error("Failed"); }
  };

  const regenKeys = async (id) => {
    if (!window.confirm("Regenerate API keys? The old keys will stop working immediately.")) return;
    try {
      const r = await b2bAPI.regenerateKeys(id);
      const { api_key, api_secret } = r.data.data;
      toast.success("New keys generated");
      alert(`New API Key:\n${api_key}\n\nNew Secret:\n${api_secret}`);
    } catch { toast.error("Failed"); }
  };

  const pages = Math.ceil(total/15);

  return (
    <div>
      <style>{`
        .b2b-mobile-cards  { display: none; }
        .b2b-desktop-table { display: block; }
        .b2b-card {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
        }
        .b2b-card:last-child { border-bottom: none; }
        @media (max-width: 768px) {
          .b2b-mobile-cards  { display: block; }
          .b2b-desktop-table { display: none; }
        }
      `}</style>

      <div className="page-header">
        <div>
          <div className="page-title">B2B Clients</div>
          <div className="page-subtitle">Third-party businesses using your delivery API</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={14}/> Refresh</button>
      </div>

      <div className="filters-bar" style={{marginBottom:12}}>
        <select className="select" value={statusFilter} onChange={e=>{setStatus(e.target.value);setPage(1);}}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      <div className="card">
        {loading ? <div className="loading-center"><div className="spinner"/></div> : (
          <>
            {/* Mobile cards */}
            <div className="b2b-mobile-cards">
              {clients.length === 0
                ? <div className="empty-state"><p>No B2B clients yet</p></div>
                : clients.map(c => (
                <div key={c.id} className="b2b-card">
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:14}}>{c.name}</div>
                      <div style={{fontSize:12,color:"var(--text3)"}}>{c.email}</div>
                    </div>
                    <span className={`badge ${c.status==="active"?"badge-active":c.status==="pending"?"badge-pending":"badge-suspended"}`}>
                      {c.status}
                    </span>
                  </div>
                  <div style={{display:"flex",gap:12,fontSize:12,color:"var(--text3)",marginBottom:8,flexWrap:"wrap"}}>
                    <span style={{textTransform:"capitalize"}}>{c.business_type||"—"}</span>
                    <span className="mono">{(c.rate_limit||1000).toLocaleString()}/hr</span>
                    {c.created_at && <span>{format(new Date(c.created_at),"MMM d, yyyy")}</span>}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {c.status==="pending" && <button className="btn btn-success btn-sm" onClick={()=>approve(c.id)}><CheckCircle size={13}/> Approve</button>}
                    {c.status==="active"  && <button className="btn btn-danger btn-sm"  onClick={()=>suspend(c.id)}><XCircle size={13}/> Suspend</button>}
                    {c.status==="suspended" && <button className="btn btn-success btn-sm" onClick={()=>approve(c.id)}>Reactivate</button>}
                    <button className="btn btn-ghost btn-sm" onClick={()=>regenKeys(c.id)}><Key size={13}/> Regen Keys</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="b2b-desktop-table table-wrap">
              <table>
                <thead><tr><th>Business</th><th>Email</th><th>Type</th><th>Rate Limit</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {clients.length===0
                    ? <tr><td colSpan={7}><div className="empty-state"><p>No B2B clients yet</p></div></td></tr>
                    : clients.map(c=>(
                    <tr key={c.id}>
                      <td className="fw-bold">{c.name}</td>
                      <td style={{color:"var(--text2)",fontSize:12}}>{c.email}</td>
                      <td style={{textTransform:"capitalize",color:"var(--text2)"}}>{c.business_type||"—"}</td>
                      <td className="mono">{(c.rate_limit||1000).toLocaleString()}/hr</td>
                      <td>
                        <span className={`badge ${c.status==="active"?"badge-active":c.status==="pending"?"badge-pending":"badge-suspended"}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="mono" style={{fontSize:11,color:"var(--text3)"}}>{c.created_at?format(new Date(c.created_at),"MMM d, yyyy"):"—"}</td>
                      <td>
                        <div style={{display:"flex",gap:4}}>
                          {c.status==="pending"   && <button className="btn btn-success btn-sm" onClick={()=>approve(c.id)}><CheckCircle size={13}/> Approve</button>}
                          {c.status==="active"    && <button className="btn btn-danger btn-sm"  onClick={()=>suspend(c.id)}><XCircle size={13}/> Suspend</button>}
                          {c.status==="suspended" && <button className="btn btn-success btn-sm" onClick={()=>approve(c.id)}>Reactivate</button>}
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>regenKeys(c.id)} title="Regenerate API Keys"><Key size={13}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <span>Showing {clients.length} of {total}</span>
              <div className="pagination-btns">
                <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Prev</button>
                <span style={{padding:"0 8px",color:"var(--text2)"}}>Page {page} of {pages||1}</span>
                <button className="btn btn-ghost btn-sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}